import * as Sentry from '@sentry/nextjs'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { useGetSearchProductsV3 } from '@/apis/rest/generated/hooks'
import {
  ProductSortByParamParameter,
  SearchProductResponseDtoV3,
  SortDirectionParamParameter,
} from '@/apis/rest/generated/types'
import { AdPetType } from '@/graphql/generated/schemas'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { ApiErrorUtils } from '@/utils/utilApiErrors'

type UseQuerySearchBrandProductsType = {
  size: number
  page: number
  sortBy: ProductSortByParamParameter
  sortDirection: SortDirectionParamParameter
  keyword: string
  petType: AdPetType
  minPrice?: number
  maxPrice?: number
  brandIds?: number[]
  categoryIds?: number[]
  includeSoldOut?: boolean
}

export const useQuerySearchBrandProducts = ({
  size,
  page,
  sortBy,
  sortDirection,
  keyword,
  petType,
  minPrice,
  maxPrice,
  brandIds,
  categoryIds,
  includeSoldOut = false,
}: UseQuerySearchBrandProductsType) => {
  const dispatch = useDispatch()
  const { data, isLoading } = useGetSearchProductsV3(
    {
      size,
      page,
      sortBy,
      sortDirection,
      keyword,
      petType,
      includeSoldOut,
      minPrice,
      maxPrice,
      brandIds,
      categoryIds,
      filterByHomeBrand: true,
      sortSoldOutToLast: true,
    },
    {
      query: {
        onError: (error: unknown) => {
          if (error instanceof AxiosError) {
            const responseData = error.response?.data.errors
            const errorInfo = ApiErrorUtils.getError(error)

            Sentry.captureException(`검색 결과 브랜드 리스트 서버 에러`, {
              extra: {
                size,
                page,
                sortBy,
                sortDirection,
                keyword,
                petType,
                includeSoldOut,
                minPrice,
                maxPrice,
                brandIds,
                categoryIds,
                filterByHomeBrand: true,
                sortSoldOutToLast: true,
                responseData,
              },
              tags: { errorMessage: errorInfo.message },
            })

            dispatch(
              doSetGlobalToastContent({
                content: `${errorInfo.message}\n오류가 지속된다면 고객센터에 문의해 주세요`,
                duration: 5,
              })
            )
          } else {
            const errorInfo = ApiErrorUtils.getError(error)
            Sentry.captureException(`검색 결과 브랜드 리스트 네트워크 에러`, {
              extra: { size, page, sortBy, sortDirection, keyword },
              tags: { errorMessage: errorInfo.message },
            })

            dispatch(
              doSetGlobalToastContent({
                content: `${errorInfo.message}\n오류가 지속된다면 고객센터에 문의해 주세요`,
                duration: 5,
              })
            )
          }
        },
        select: (data): SearchProductResponseDtoV3 => {
          /**
           * 상품 준비 상태인 상품은 제외
           * isSuspend가 true인 상품은 제외
           * 상품결과에서 20개가 안보여질 수가 있음
           */
          const filteredProducts = data?.products?.filter((product) => !product.isSuspend)
          return {
            ...data,
            products: filteredProducts,
          }
        },
      },
    }
  )

  return {
    data,
    isLoading,
  }
}
